import { Email } from "@/Common/Api/Contacts/Dto/Email";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { ContactPointType } from "@/Common/Models/Contacts/Enums/ContactPointType";
import { CheckboxLabel } from "@/DesignSystem";
import { IEmailAddressesModel } from "../Models/UI/IEmailAddressesModel";

class ViewEmailAddressesState {
    public email: Email;
    public isNotifiedByEmail: boolean;

    public static getDefault(): ViewEmailAddressesState {
        let s = new ViewEmailAddressesState();
        s.email = Email.newEmail(ContactPointType.Work);
        s.isNotifiedByEmail = false;
        return s;
    }

    public static from(emails: Email[], isNotifiedByEmail: boolean): ViewEmailAddressesState {
        let s = new ViewEmailAddressesState();
        s.email = MyleHelper.CloneObject(emails.firstPreferredOrDefault(p => p.IsPreferred));
        s.isNotifiedByEmail = isNotifiedByEmail;

        return s;
    }
}

interface IViewEmailAddressesProps {
    currentlyProcessing: boolean;
    model: IEmailAddressesModel;
    onVerify?: (emailAddress: string, contactPointId: string, verificationCode: string) => void;
    onResend: (emailAddress: string, contactPointId: string) => void;
    onChangeIsOkToContactAndNotifiedByEmail: (emails: Email[], isNotifiedByEmail: boolean) => void;
    serverError?: string;
}

class ViewEmailAddressesControl extends BaseReactComponent<IViewEmailAddressesProps, ViewEmailAddressesState> {
    public constructor(props: IViewEmailAddressesProps) {
        super(props);

        let initialState: ViewEmailAddressesState;
        let model: IEmailAddressesModel;

        if (this.props.model)
            model = this.props.model;

        const emails = model.get("Emails").toArray();
        const isNotified = model.get("IsNotifiedByEmail");

        if (emails.length == 0) {
            initialState = ViewEmailAddressesState.getDefault();
        }
        else {
            initialState = ViewEmailAddressesState.from(emails, isNotified);
        }
        this.state = initialState;
    }

    public componentDidUpdate(prevProps: Readonly<IViewEmailAddressesProps>): void {
        if (prevProps.model !== this.props.model) {
            const model: IEmailAddressesModel = this.props.model;
            const emails = model.get("Emails");
            const isNotified = model.get("IsNotifiedByEmail");

            const currentEmails = prevProps.model.get("Emails");
            const currentIsNotified = prevProps.model.get("IsNotifiedByEmail");

            if (emails !== currentEmails || isNotified !== currentIsNotified) {
                const firstEmail = emails?.toArray()?.[0];
                if (this.state.isNotifiedByEmail !== isNotified || this.state.email?.ContactPoint_id !== firstEmail?.ContactPoint_id) {
                    this.mutateState(s => {
                        s.isNotifiedByEmail = isNotified;
                        if (emails) {
                            s.email = firstEmail
                                ? MyleHelper.CloneObject(firstEmail)
                                : Email.newEmail(ContactPointType.Work);
                        }
                    });
                }
            }
        }
    }

    public render(): JSX.Element {
        let emailsRender: JSX.Element[] = [];

        if (!IsNullOrWhiteSpace(this.state.email.Address)) {
            let emailDetails = (
                <div key="addresses">
                    <div>
                        {this.state.email.IsVerified &&
                            <span title={cacheStore.GetString("VerifiedLabel")} className="glyphicon glyphicon-ok green-text" style={{ marginRight: "5px" }} />
                        }
                        <strong><a href={"mailto:" + this.state.email.Address}>{MyleHelper.ReplaceEmptyString(this.state.email.Address)}</a></strong>
                    </div>
                    <div>
                        <CheckboxLabel
                            id={"chkIsOk" + this.state.email.ContactPoint_id}
                            checked={this.state.email.IsOkToContact}
                            label={cacheStore.GetString("OkToContactLabel")}
                            onChange={() => this.onChangeIsOkToContact()}
                            disabled={!this.state.email.IsValid}
                        />
                    </div>

                    <div>
                        <CheckboxLabel
                            id={"IsNotifiedByEmail" + this.state.email.ContactPoint_id}
                            checked={this.state.isNotifiedByEmail}
                            label={cacheStore.GetString("SendUpdateNotificationByEmailLabel")}
                            onChange={isChecked => this.onChangeNotifyByEmail(isChecked)}
                            disabled={!this.state.email.IsValid}
                        />
                    </div>

                    {!this.state.email.IsVerified && (
                        <>
                            <div className="row" style={{ marginBottom: "5px", marginLeft: "0px" }}>
                                <div className="col-lg-1" style={{ padding: "0", paddingTop: "5px" }}>
                                    <span> {cacheStore.GetString("VerificationLabel")}</span>
                                </div>
                                <div className="col-lg-6" style={{ marginLeft: "25px" }}>
                                    <TextboxControl
                                        debounceMs={200}
                                        style={{ minWidth: "200px" }}
                                        value={this.state.email.VerificationCode}
                                        onChange={e => this.onCodeChange(e)}
                                        placeholder=""
                                        id={"Code_id" + this.state.email.ContactPoint_id}
                                    />
                                </div>
                            </div>
                            <div>
                                <button
                                    disabled={IsNullOrWhiteSpace(this.state.email.VerificationCode) || this.props.currentlyProcessing}
                                    style={{ width: "95px", paddingTop: "5px" }}
                                    onClick={() => this.verifyEmail()}
                                    className="btn btn-primary"
                                >
                                    {cacheStore.GetString("VerifyEmailLabel")}
                                </button>
                                <button
                                    disabled={this.props.currentlyProcessing}
                                    style={{ width: "95px", paddingTop: "5px", marginLeft: "10px" }}
                                    onClick={() => this.resendEmail()}
                                    className="btn btn-primary"
                                >
                                    {cacheStore.GetString("ResendLabel")}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            );

            emailsRender.push(emailDetails);
        }
        else {
            var noEmails: JSX.Element = (
                <div key="email">
                    <div style={{ textAlign: "left" }}><span><strong>--</strong></span></div>
                </div>
            );
            emailsRender.push(noEmails);
        }

        return (
            <div>
                <div>
                    {emailsRender}
                </div>
                {!IsNullOrWhiteSpace(this.props.serverError) && (
                    <div className="error-message">
                        <label className="label-error"> {cacheStore.GetString(this.props.serverError)} </label>
                    </div>
                )}
            </div>
        );
    }

    private onChangeIsOkToContact(): void {
        this.mutateState(
            s => s.email.IsOkToContact = !s.email.IsOkToContact,
            () => this.saveEmails()
        );
    }

    private onChangeNotifyByEmail(isChecked: boolean): void {
        this.mutateState(
            s => s.isNotifiedByEmail = isChecked,
            () => this.saveEmails()
        );
    }

    private saveEmails(): void {
        if (this.props.onChangeIsOkToContactAndNotifiedByEmail) {
            const emailsToSave = this.state.email.IsValid && !IsNullOrWhiteSpace(this.state.email.Address)
                ? [this.state.email]
                : [];

            this.props.onChangeIsOkToContactAndNotifiedByEmail(emailsToSave, this.state.isNotifiedByEmail);
        }
    }

    private onCodeChange(code: string): void {
        this.mutateState(s => s.email.VerificationCode = code);
    }

    private verifyEmail(): void {
        if (this.props.onVerify) {
            const verificationCode = this.state.email.VerificationCode;
            this.mutateState(
                s => s.email.VerificationCode = "",
                () => this.props.onVerify(this.state.email.Address, this.state.email.ContactPoint_id, verificationCode)
            );
        }
    }

    private resendEmail(): void {
        if (this.props.onResend) {
            this.props.onResend(this.state.email.Address, this.state.email.ContactPoint_id);
        }
    }
}

export { ViewEmailAddressesControl };
